import { LogOut, XIcon } from 'lucide-react';

import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from 'shared-ui';

import { useMediaQuery } from '@/hooks/useMediaQuery';

interface SignOutDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
}

export function SignOutDialog({ open, onOpenChange, onConfirm }: SignOutDialogProps) {
    const isDesktop = useMediaQuery('(min-width: 768px)');

    const handleConfirm = () => {
        onOpenChange(false);
        onConfirm();
    };

    const handleCancel = () => {
        onOpenChange(false);
    };

    if (isDesktop) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="rounded-md p-6 pt-0 sm:max-w-sm" showCloseButton={false}>
                    <DialogHeader className="border-border -mx-6 flex flex-row items-center justify-between border-b px-6 py-6">
                        <DialogTitle className="text-2xl font-extrabold tracking-wide uppercase">Sign Out</DialogTitle>
                        <DialogDescription className="sr-only">Are you sure you want to sign out?</DialogDescription>
                        <Tooltip>
                            <DialogClose
                                render={
                                    <TooltipTrigger
                                        render={
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                className="size-10 shrink-0 rounded-md"
                                            />
                                        }
                                    />
                                }
                            >
                                <XIcon className="size-4" strokeWidth={2.25} />
                                <span className="sr-only">Close</span>
                            </DialogClose>
                            <TooltipContent side="bottom" align="center">
                                Close
                            </TooltipContent>
                        </Tooltip>
                    </DialogHeader>
                    <p className="text-foreground text-base leading-6 font-bold">Are you sure you want to sign out?</p>
                    <div className="flex flex-col gap-2">
                        <Button className="h-10 w-full" onClick={handleConfirm}>
                            <LogOut className="size-4" />
                            Sign Out
                        </Button>
                        <Button variant="outline" className="h-10 w-full" onClick={handleCancel}>
                            Cancel
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Drawer open={open} onOpenChange={onOpenChange}>
            <DrawerContent className="border-border border">
                <div className="px-4 pt-8 pb-8">
                    <DrawerHeader className="mb-4 flex flex-row items-center justify-between space-y-0 !p-0 text-left">
                        <DrawerTitle className="text-foreground text-xl leading-7 font-black tracking-normal uppercase">
                            Sign Out
                        </DrawerTitle>
                        <DrawerDescription className="sr-only">Are you sure you want to sign out?</DrawerDescription>
                        <DrawerClose asChild>
                            <Button variant="ghost" size="icon-sm" className="size-10 shrink-0 rounded-md">
                                <XIcon className="size-4" strokeWidth={2.25} />
                                <span className="sr-only">Close</span>
                            </Button>
                        </DrawerClose>
                    </DrawerHeader>
                    <p className="text-foreground mb-4 text-base leading-6 font-bold">
                        Are you sure you want to sign out?
                    </p>
                    <DrawerFooter className="flex-col gap-2 !p-0">
                        <Button className="h-10 w-full" onClick={handleConfirm}>
                            <LogOut className="size-4" />
                            Sign Out
                        </Button>
                        <Button variant="outline" className="h-10 w-full" onClick={handleCancel}>
                            Cancel
                        </Button>
                    </DrawerFooter>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
